import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Shield, Clock, Lock, ChevronDown, Brain, FileText, Sparkles, Heart, AlertCircle } from 'lucide-react'

function LandingPage() {
  const navigate = useNavigate()
  const [input, setInput] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')


  const wordCount = input.trim().split(/\s+/).filter(Boolean).length
  const minWords = 20
  const maxWords = 1000

  const handleSubmit = async () => {
    setError('')
    if (wordCount < minWords) {
      setError(`Try adding a bit more detail. How does stress show up in your body? Your sleep? Your mood? More detail = better insights. (${wordCount}/${minWords} words)`)
      return
    }
    if (wordCount > maxWords) {
      setError('Your input is quite long. Please keep it under 1000 words for the best analysis.')
      return
    }

    setIsSubmitting(true)
    try {
      const sessionId = localStorage.getItem('stresslens_session') || crypto.randomUUID()
      localStorage.setItem('stresslens_session', sessionId)

      const res = await fetch('/api/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ input_text: input, session_id: sessionId })
      })

      const data = await res.json()
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Analysis failed')
      }

      // Store result locally for the result page
      localStorage.setItem(`stresslens_result_${data.assessment_id}`, JSON.stringify(data.result))
      navigate(`/result?id=${data.assessment_id}`)
    } catch (err: any) {
      setError(err.message || 'We couldn\'t complete your assessment right now. Please try again in a moment.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation */}
      <nav className="w-full bg-bg/80 backdrop-blur-sm border-b border-border sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-text text-lg tracking-tight">StressLens</span>
          </div>
          <div className="hidden sm:flex items-center gap-6 text-sm text-text-light">
            <button onClick={() => scrollTo('how-it-works')} className="hover:text-text transition-colors">How It Works</button>
            <button onClick={() => scrollTo('pricing')} className="hover:text-text transition-colors">Pricing</button>
            <button onClick={() => scrollTo('faq')} className="hover:text-text transition-colors">FAQ</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <main className="flex-1">
        <section className="pt-12 pb-16 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium mb-6">
              <Shield className="w-3.5 h-3.5" />
              Evidence-based · Private · No sign-up
            </div>
            <h1 className="text-4xl sm:text-5xl font-semibold text-text tracking-tight leading-[1.15] mb-4">
              Understand Your Stress<br className="hidden sm:block" /> in <span className="text-primary">3 Minutes</span>
            </h1>
            <p className="text-lg text-text-light leading-relaxed max-w-xl mx-auto mb-10">
              Describe how you're feeling. Our AI analyzes your responses using clinical CBT and DBT frameworks. 
              Get an instant report — not a conversation with a bot.
            </p>

            {/* Input Card */}
            <div className="bg-white rounded-2xl border border-border shadow-sm p-5 sm:p-6 text-left">
              <label className="block text-sm font-medium text-text mb-2">
                What's going on right now?
              </label>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="I've been feeling overwhelmed at work lately. I can't sleep well — wake up at 3am with racing thoughts. I feel irritable with my family, and I can't focus on anything during the day..."
                className="w-full h-40 p-4 rounded-xl border border-border bg-bg resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all text-text placeholder:text-text-light/60 text-base leading-relaxed"
              />
              <div className="flex items-center justify-between mt-3">
                <span className={`text-xs ${wordCount >= minWords ? 'text-mild' : 'text-text-light'}`}>
                  {wordCount} words {wordCount < minWords && `(min ${minWords})`}
                </span>
                {error && (
                  <span className="text-xs text-severe flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {error}
                  </span>
                )}
              </div>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full mt-4 bg-accent hover:bg-accent-dark disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium py-3.5 rounded-xl transition-colors text-base flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <span className="w-2 h-2 rounded-full bg-white/70 animate-pulse-dot" />
                    <span className="w-2 h-2 rounded-full bg-white/70 animate-pulse-dot-delay-1" />
                    <span className="w-2 h-2 rounded-full bg-white/70 animate-pulse-dot-delay-2" />
                    Analyzing...
                  </>
                ) : (
                  <>Analyze My Stress</>
                )}
              </button>
              <div className="flex items-center justify-center gap-4 mt-3 text-xs text-text-light">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Free</span>
                <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> No sign-up</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Takes 3 min</span>
              </div>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center justify-center gap-6 mt-10 text-xs text-text-light">
              <div className="flex items-center gap-1.5">
                <Brain className="w-4 h-4 text-primary" />
                CBT & DBT Frameworks
              </div>
              <div className="flex items-center gap-1.5">
                <Lock className="w-4 h-4 text-primary" />
                Private & Encrypted
              </div>
              <div className="flex items-center gap-1.5">
                <FileText className="w-4 h-4 text-primary" />
                Structured Report
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="py-16 px-4 bg-white border-y border-border">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-semibold text-text text-center mb-12">How It Works</h2>
            <div className="grid sm:grid-cols-3 gap-8">
              {[
                {
                  num: '01',
                  title: 'Write Freely',
                  desc: 'Describe how you\'ve been feeling, what\'s bothering you, any physical symptoms. No multiple choice. No word bubbles. Just write.',
                  icon: FileText
                },
                {
                  num: '02',
                  title: 'AI Analysis',
                  desc: 'Our system analyzes your responses using CBT and DBT frameworks to identify patterns, cognitive distortions, and stress triggers.',
                  icon: Brain
                },
                {
                  num: '03',
                  title: 'Get Your Report',
                  desc: 'A clear, structured report with your stress level, identified patterns, 3 immediate actions, and a 7-day plan.',
                  icon: Sparkles
                }
              ].map((step) => (
                <div key={step.num} className="text-center">
                  <div className="w-12 h-12 rounded-2xl bg-bg border border-border flex items-center justify-center mx-auto mb-4">
                    <step.icon className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-xs font-semibold text-primary mb-2 block">{step.num}</span>
                  <h3 className="font-semibold text-text mb-2">{step.title}</h3>
                  <p className="text-sm text-text-light leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Social Proof */}
        <section className="py-16 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-mild/10 text-mild text-xs font-medium mb-8">
              <Heart className="w-3.5 h-3.5" />
              Informed by evidence-based CBT & DBT frameworks
            </div>
            <p className="text-text-light text-sm max-w-lg mx-auto leading-relaxed">
              StressLens is designed as a self-assessment tool, not a chatbot. 
              We believe you deserve a clear report when you need it — not an endless conversation.
            </p>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="py-16 px-4 bg-white border-y border-border">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-semibold text-text text-center mb-10">Simple Pricing</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-border p-6 bg-bg">
                <div className="text-xs font-semibold text-text-light uppercase tracking-wider mb-2">Free</div>
                <div className="text-3xl font-semibold text-text mb-4">$0</div>
                <ul className="space-y-3 text-sm text-text-light">
                  <li className="flex items-start gap-2"><span className="text-mild mt-0.5">✓</span> Full stress level assessment</li>
                  <li className="flex items-start gap-2"><span className="text-mild mt-0.5">✓</span> One-sentence summary</li>
                  <li className="flex items-start gap-2"><span className="text-mild mt-0.5">✓</span> Cognitive pattern analysis</li>
                  <li className="flex items-start gap-2"><span className="text-mild mt-0.5">✓</span> First immediate action</li>
                  <li className="flex items-start gap-2"><span className="text-mild mt-0.5">✓</span> Days 1-2 of your 7-day plan</li>
                </ul>
                <div className="mt-6 text-xs text-text-light">No credit card required.</div>
              </div>
              <div className="rounded-2xl border-2 border-accent p-6 bg-white relative">
                <div className="absolute -top-3 left-6 px-2 py-0.5 bg-accent text-white text-[10px] font-bold uppercase tracking-wider rounded">
                  Full Report
                </div>
                <div className="text-xs font-semibold text-text-light uppercase tracking-wider mb-2">One-time</div>
                <div className="text-3xl font-semibold text-text mb-4">$3.99</div>
                <ul className="space-y-3 text-sm text-text-light">
                  <li className="flex items-start gap-2"><span className="text-accent mt-0.5">✓</span> Everything in Free</li>
                  <li className="flex items-start gap-2"><span className="text-accent mt-0.5">✓</span> Complete cognitive analysis</li>
                  <li className="flex items-start gap-2"><span className="text-accent mt-0.5">✓</span> All 3 immediate actions</li>
                  <li className="flex items-start gap-2"><span className="text-accent mt-0.5">✓</span> Full 7-day plan</li>
                  <li className="flex items-start gap-2"><span className="text-accent mt-0.5">✓</span> When-to-seek-help guidance</li>
                </ul>
                <div className="mt-6 text-xs text-text-light">Pay once, not every month. Access anytime.</div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-16 px-4">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-semibold text-text text-center mb-10">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {[
                {
                  q: 'Is this therapy?',
                  a: 'No. StressLens is a self-assessment tool based on CBT/DBT frameworks. It helps you understand your stress patterns. It is not a clinical diagnosis and does not replace a licensed therapist. If you\'re in crisis, call 988.'
                },
                {
                  q: 'Do I need to sign up?',
                  a: 'No. You can take one free assessment without creating an account. You only need to enter your email when you choose to unlock the full report.'
                },
                {
                  q: 'Is my data private?',
                  a: 'Yes. Your entries are encrypted. We don\'t share or sell data. Free assessments are stored only by anonymous session ID.'
                },
                {
                  q: 'How is this different from Wysa or Youper?',
                  a: 'Those are chatbots designed for ongoing daily conversations. StressLens is a one-time assessment — like a blood test, not a gym membership. Use it when you need it.'
                },
                {
                  q: 'What if the AI detects I\'m in crisis?',
                  a: 'The system includes risk detection. If your responses suggest you may be in crisis, your report will prioritize crisis resources (988, Crisis Text Line) above all other content.'
                }
              ].map((faq, i) => (
                <details key={i} className="group bg-white rounded-xl border border-border overflow-hidden">
                  <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                    <span className="font-medium text-text text-sm">{faq.q}</span>
                    <ChevronDown className="w-4 h-4 text-text-light group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="px-4 pb-4 text-sm text-text-light leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-border py-8 px-4">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-text-light">
          <div className="flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span className="font-medium text-text">StressLens</span>
          </div>
          <div className="flex items-center gap-4">
            <span>Privacy Policy</span>
            <span>Terms of Service</span>
            <span>Contact</span>
            <span className="text-severe font-medium">Crisis: 988</span>
          </div>
        </div>
        <div className="max-w-3xl mx-auto mt-4 text-[10px] text-text-light/60 text-center">
          This tool is for self-care and wellness purposes only. It is not a medical diagnosis.
          If you are experiencing a medical emergency, call 911 or go to your nearest emergency room.
        </div>
      </footer>
    </div>
  )
}

export default LandingPage
